import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../api";
import type { AdminUser, AiUsageSummary, InviteCode, InviteRedemption, ProviderSlot } from "../lib/types";
import { useUI } from "../lib/theme";
import { CollapsibleSection } from "./ui";

/**
 * Panel de admin (solo para el dueño): gestiona el cupo de atletas de la fuente, los códigos de
 * invitación de la beta y el acceso/tipo de cada usuario. Se abre desde el menú de Ajustes.
 */
export function AdminPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useUI();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [slots, setSlots] = useState<ProviderSlot[]>([]);
  const [aiUsage, setAiUsage] = useState<AiUsageSummary | null>(null);

  const load = useCallback(async (withSpinner: boolean) => {
    if (withSpinner) setLoading(true);
    try {
      const [u, c, ai] = await Promise.all([adminApi.users(), adminApi.inviteCodes(), adminApi.aiUsage()]);
      setUsers(u.users);
      setSlots(u.slots);
      setCodes(c.codes);
      setAiUsage(ai);
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    await load(false);
  }, [load]);

  useEffect(() => {
    if (open) void load(true);
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-full max-w-2xl flex-col border-l border-line bg-panel shadow-2xl">
        <header className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="text-base font-bold">{t("admin_title")}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-muted transition hover:bg-panel2 hover:text-fg"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5">
          {loading ? (
            <p className="text-muted">{t("admin_loading")}</p>
          ) : (
            <>
              <CollapsibleSection title={t("admin_users")} defaultOpen>
                <UsersSection users={users} onChanged={reload} />
              </CollapsibleSection>
              <CollapsibleSection title={t("admin_codes")}>
                <CodesSection codes={codes} users={users} onChanged={reload} />
              </CollapsibleSection>
              {aiUsage && (
                <CollapsibleSection title={t("admin_ai_usage")}>
                  <AiUsageSection usage={aiUsage} />
                </CollapsibleSection>
              )}
              <CollapsibleSection title={t("admin_slots")}>
                <SlotsCard slots={slots} />
              </CollapsibleSection>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function SlotsCard({ slots }: { slots: ProviderSlot[] }) {
  const { t } = useUI();
  if (slots.length === 0) {
    return <p className="text-sm text-muted">{t("admin_no_slots")}</p>;
  }
  return (
    <div className="space-y-3">
      {slots.map((slot) => {
        const pct = Math.min(100, Math.round((slot.used / Math.max(1, slot.cap)) * 100));
        const full = slot.used >= slot.cap;
        return (
          <section key={slot.provider} className="rounded-2xl bg-panel2/40 p-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-fg">{slot.displayName}</span>
              <span className={`text-lg font-bold ${full ? "text-red-500" : "text-accent"}`}>
                {slot.used}/{slot.cap}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted">{t("admin_slots_desc", { cap: slot.cap })}</p>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-line">
              <div className={`h-full ${full ? "bg-red-500" : "bg-accent"}`} style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-[11px] leading-snug text-muted">{t("admin_slots_note")}</p>
          </section>
        );
      })}
    </div>
  );
}

/** Resumen de uso de IA (Coach): totales globales + top usuarios. Caracteres → tokens estimados (chars/4). */
function AiUsageSection({ usage }: { usage: AiUsageSummary }) {
  const { t, loc } = useUI();
  const toTokens = (chars: number) => Math.round(chars / 4);
  const fmt = (chars: number) => toTokens(chars).toLocaleString(loc === "en" ? "en-US" : "es-ES");
  const named = (u: AiUsageSummary["perUser"][number]) =>
    [u.firstname, u.lastname].filter(Boolean).join(" ").trim() || t("admin_ai_anon");
  const totals: { key: string; chars: number }[] = [
    { key: "admin_ai_today", chars: usage.totals.todayChars },
    { key: "admin_ai_week", chars: usage.totals.weekChars },
    { key: "admin_ai_month", chars: usage.totals.monthChars },
    { key: "admin_ai_all", chars: usage.totals.allChars },
  ];

  return (
    <section className="rounded-2xl bg-panel2/40 p-4">
      <p className="mt-0.5 text-xs text-muted">{t("admin_ai_usage_desc")}</p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {totals.map((it) => (
          <div key={it.key} className="rounded-xl border border-line bg-panel px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">{t(it.key)}</div>
            <div className="mt-0.5 text-base font-bold text-accent">{fmt(it.chars)}</div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted">{t("admin_ai_tokens_est")}</p>
      {usage.perUser.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-xl border border-line">
          <table className="w-full text-left text-xs">
            <thead className="bg-panel2/60 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="px-3 py-2 font-semibold">{t("admin_ai_user")}</th>
                <th className="px-3 py-2 text-right font-semibold">{t("admin_ai_today")}</th>
                <th className="px-3 py-2 text-right font-semibold">{t("admin_ai_week")}</th>
                <th className="px-3 py-2 text-right font-semibold">{t("admin_ai_all")}</th>
                <th className="px-3 py-2 text-right font-semibold">{t("admin_ai_requests")}</th>
              </tr>
            </thead>
            <tbody>
              {usage.perUser.map((u) => (
                <tr key={u.user_id} className="border-t border-line">
                  <td className="px-3 py-1.5 text-fg">{named(u)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted">{fmt(u.today_chars)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted">{fmt(u.week_chars)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted">{fmt(u.total_chars)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted">
                    {u.requests.toLocaleString(loc === "en" ? "en-US" : "es-ES")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TypeBadge({ type }: { type: string }) {
  const { t } = useUI();
  const coach = type === "coach";
  return (
    <span
      className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
        coach ? "bg-violet-500/15 text-violet-500" : "bg-accent/15 text-accent"
      }`}
    >
      {coach ? t("admin_type_coach") : t("admin_type_athlete")}
    </span>
  );
}

function InviteCodeRow({
  code,
  onChanged,
}: {
  code: InviteCode;
  onChanged: () => void;
}) {
  const { t, lang } = useUI();
  const [copied, setCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [redemptions, setRedemptions] = useState<InviteRedemption[] | null>(null);
  const [loadingR, setLoadingR] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(code.code); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* ignore */ }
  };

  const copyUrl = async () => {
    const url = `${window.location.origin}/?invite=${code.code}`;
    try { await navigator.clipboard.writeText(url); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 1500); }
    catch { /* ignore */ }
  };

  const toggle = async () => {
    if (!expanded && redemptions === null) {
      setLoadingR(true);
      adminApi.inviteRedemptions(code.code)
        .then((r) => setRedemptions(r.redemptions))
        .catch(() => setRedemptions([]))
        .finally(() => setLoadingR(false));
    }
    setExpanded((v) => !v);
  };

  const isDisabled = code.status === "disabled";
  const expired = code.expires_at ? new Date(code.expires_at) < new Date() : false;
  const exhausted = code.max_uses !== null && code.uses >= code.max_uses;

  const statusColor = isDisabled || expired || exhausted
    ? "text-amber-500"
    : "text-emerald-500";

  const statusLabel = isDisabled
    ? t("admin_st_disabled")
    : expired
      ? (lang === "es" ? "Caducado" : "Expired")
      : exhausted
        ? (lang === "es" ? "Agotado" : "Exhausted")
        : t("admin_st_active");

  const kindBadge = code.kind === "beta"
    ? <span className="rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent">BET</span>
    : <span className="rounded-full bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-500">CCH</span>;

  return (
    <li className="rounded-xl border border-line bg-panel px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={copy} title={t("admin_copy")} className="font-mono text-sm font-bold tracking-wider text-fg transition hover:text-accent">
          {copied ? t("admin_copied") : code.code}
        </button>
        {kindBadge}
        {code.account_type && <TypeBadge type={code.account_type} />}
        {code.kind === "coach_link" && (
          <span className="text-[11px] text-muted">
            {[code.coach_firstname, code.coach_lastname].filter(Boolean).join(" ") || code.coach_user_id?.slice(0, 8)}
          </span>
        )}
        <span className={`text-[11px] font-medium ${statusColor}`}>{statusLabel}</span>
        <span className="text-[11px] text-muted">{code.uses}/{code.max_uses ?? "∞"}</span>
        {code.expires_at && (
          <span className="text-[11px] text-muted">exp {code.expires_at.slice(0, 10)}</span>
        )}
        {code.note && <span className="text-[11px] text-muted">— {code.note}</span>}
        <button
          onClick={copyUrl}
          title={lang === "es" ? "Copiar enlace de invitación" : "Copy invite link"}
          className="text-[11px] text-muted transition hover:text-accent"
        >
          {copiedUrl ? "✓ URL" : "🔗 URL"}
        </button>
        <button
          onClick={toggle}
          className="ml-auto text-[11px] text-muted transition hover:text-fg"
        >
          {expanded ? "▲" : "▼"} {lang === "es" ? "canjes" : "uses"} ({code.uses})
        </button>
        {!isDisabled && (
          <button
            onClick={async () => { await adminApi.setInviteCodeStatus(code.code, "disabled"); onChanged(); }}
            className="rounded-md border border-line px-2 py-0.5 text-[11px] font-medium text-muted transition hover:text-fg"
          >
            {t("admin_disable")}
          </button>
        )}
        {isDisabled && (
          <button
            onClick={async () => { await adminApi.setInviteCodeStatus(code.code, "active"); onChanged(); }}
            className="rounded-md border border-line px-2 py-0.5 text-[11px] font-medium text-muted transition hover:text-fg"
          >
            {t("admin_enable")}
          </button>
        )}
        <button
          onClick={async () => {
            if (confirm(t("admin_code_delete_confirm"))) {
              await adminApi.deleteInviteCode(code.code);
              onChanged();
            }
          }}
          title={t("admin_code_delete")}
          className="rounded-md border border-red-500/30 px-2 py-0.5 text-[11px] font-medium text-red-500 transition hover:bg-red-500/10"
        >
          🗑
        </button>
      </div>
      {expanded && (
        <div className="mt-2 rounded-lg bg-panel/60 px-3 py-2 text-[11px]">
          {loadingR && <p className="text-muted">{t("admin_loading")}</p>}
          {!loadingR && redemptions?.length === 0 && (
            <p className="text-muted">{lang === "es" ? "Sin canjes todavía." : "No redemptions yet."}</p>
          )}
          {!loadingR && redemptions && redemptions.length > 0 && (
            <ul className="space-y-0.5">
              {redemptions.map((r) => {
                const name = [r.user_firstname, r.user_lastname].filter(Boolean).join(" ") || r.user_id.slice(0, 12);
                const providers = (r.user_providers ?? "").split(",").filter(Boolean);
                const hasEmail = !!r.user_has_email;
                const authMethods = [
                  ...(hasEmail ? ["email"] : []),
                  ...providers,
                ];
                return (
                  <li key={r.id} className="flex flex-wrap items-center gap-2 text-muted">
                    <span>{r.redeemed_at.slice(0, 10)}</span>
                    <span className="font-medium text-fg">{name}</span>
                    {authMethods.map((m) => (
                      <span key={m} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                        m === "strava" ? "bg-orange-500/15 text-orange-600"
                        : m === "google" ? "bg-blue-500/15 text-blue-600"
                        : "bg-panel2 text-muted border border-line"
                      }`}>
                        {m}
                      </span>
                    ))}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function CodesSection({
  codes,
  users,
  onChanged,
}: {
  codes: InviteCode[];
  users: AdminUser[];
  onChanged: () => void;
}) {
  const { t, lang } = useUI();
  const [kind, setKind] = useState<"beta" | "coach_link">("beta");
  const [prefix, setPrefix] = useState("BET");
  const [accountType, setAccountType] = useState<"athlete" | "coach">("athlete");
  const [coachUserId, setCoachUserId] = useState("");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState<number | "">(1);
  const [quantity, setQuantity] = useState<number>(1);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<"all" | "beta" | "coach_link">("all");
  const [formError, setFormError] = useState<string | null>(null);

  const coaches = users.filter((u) => u.account_type === "coach");

  const create = async () => {
    const pfx = prefix.trim().toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
    if (pfx.length !== 3) {
      setFormError(lang === "es" ? "El prefijo debe tener exactamente 3 letras A-Z." : "Prefix must be exactly 3 letters A-Z.");
      return;
    }
    if (kind === "coach_link" && !coachUserId) {
      setFormError(lang === "es" ? "Selecciona un coach." : "Select a coach.");
      return;
    }
    setFormError(null);
    setCreating(true);
    try {
      const qty = Math.max(1, Math.min(quantity, 50));
      await Promise.all(
        Array.from({ length: qty }, () =>
          adminApi.createInviteCode({
            kind,
            prefix: pfx,
            accountType: kind === "beta" ? accountType : undefined,
            coachUserId: kind === "coach_link" ? coachUserId : undefined,
            note: note.trim() || undefined,
            expiresAt: expiresAt || undefined,
            maxUses: maxUses === "" ? null : maxUses > 0 ? maxUses : null,
          }),
        ),
      );
      setNote("");
      setExpiresAt("");
      onChanged();
    } catch {
      setFormError(lang === "es" ? "Error al crear el código." : "Error creating code.");
    } finally {
      setCreating(false);
    }
  };

  const filtered = codes.filter((c) => filter === "all" || c.kind === filter);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {(["all", "beta", "coach_link"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition ${
                filter === f ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"
              }`}
            >
              {f === "all" ? (lang === "es" ? "Todos" : "All") : f === "beta" ? "BET" : "CCH"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-line bg-panel2/40 p-3">
        {/* Tipo de código */}
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          {lang === "es" ? "Tipo" : "Type"}
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value as "beta" | "coach_link"); setCoachUserId(""); }}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
          >
            <option value="beta">{t("admin_code_kind_beta")}</option>
            <option value="coach_link">{t("admin_code_kind_coach")}</option>
          </select>
        </label>

        {/* Prefijo (3 letras A-Z) */}
        <label className="flex w-28 flex-col gap-1 text-[11px] text-muted">
          {t("admin_prefix")}
          <input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
            maxLength={3}
            placeholder={t("admin_prefix_hint")}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm font-mono text-fg"
          />
        </label>

        {/* Tipo de cuenta: solo para beta */}
        {kind === "beta" && (
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            {lang === "es" ? "Tipo cuenta" : "Account type"}
            <select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value as "athlete" | "coach")}
              className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
            >
              <option value="athlete">{t("admin_type_athlete")}</option>
              <option value="coach">{t("admin_type_coach")}</option>
            </select>
          </label>
        )}

        {/* Coach selector: solo para coach_link */}
        {kind === "coach_link" && (
          <label className="flex flex-col gap-1 text-[11px] text-muted">
            {t("admin_code_coach_label")}
            <select
              value={coachUserId}
              onChange={(e) => setCoachUserId(e.target.value)}
              className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
            >
              <option value="">{lang === "es" ? "Seleccionar…" : "Select…"}</option>
              {coaches.map((c) => (
                <option key={c.id} value={c.id}>
                  {[c.firstname, c.lastname].filter(Boolean).join(" ") || c.id}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Usos máximos (vacío = ilimitado) */}
        <label className="flex w-24 flex-col gap-1 text-[11px] text-muted">
          {lang === "es" ? "Usos máx. (vacío=∞)" : "Max uses (empty=∞)"}
          <input
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value === "" ? "" : parseInt(e.target.value) || 1)}
            placeholder={t("admin_code_max_unlimited")}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
          />
        </label>

        {/* Caducidad (vacío = sin límite) */}
        <label className="flex flex-col gap-1 text-[11px] text-muted">
          {lang === "es" ? "Caduca (vacío=nunca)" : "Expires (empty=never)"}
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
          />
        </label>

        <label className="flex flex-1 flex-col gap-1 text-[11px] text-muted">
          {t("admin_note")}
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
          />
        </label>

        <label className="flex w-20 flex-col gap-1 text-[11px] text-muted">
          {lang === "es" ? "Cantidad" : "Quantity"}
          <input
            type="number"
            min={1}
            max={50}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
            className="rounded-lg border border-line bg-ink/40 px-2 py-1.5 text-sm text-fg"
          />
        </label>

        <div className="flex flex-col gap-1">
          <button
            onClick={create}
            disabled={creating}
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
          >
            {creating ? (lang === "es" ? "Creando…" : "Creating…") : (quantity > 1 ? `${t("admin_create_code")} ×${quantity}` : t("admin_create_code"))}
          </button>
          {formError && <p className="text-[11px] text-red-500">{formError}</p>}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted">{t("admin_no_codes")}</p>
      ) : (
        <ul className="space-y-1.5">
          {filtered.map((code) => (
            <InviteCodeRow key={code.code} code={code} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </section>
  );
}

function UsersSection({ users, onChanged }: { users: AdminUser[]; onChanged: () => void }) {
  const { t, lang } = useUI();
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (id: string, fn: () => Promise<void>) => {
    setBusy(id);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  // Tras revocar/eliminar, avisa según si pudimos soltar el slot en la fuente de verdad:
  //  - deauthorized: hecho, el contador de la fuente bajará.
  //  - hadToken pero falló: reintento manual desde el proveedor.
  //  - sin token (ya estaba desconectado en Westive): solo el atleta puede revocar desde SU proveedor.
  const notifySourceRevoke = (res: { deauthorized: boolean; hadToken: boolean }) => {
    if (res.deauthorized) return;
    alert(res.hadToken ? t("admin_revoke_manual") : t("admin_revoke_notoken"));
  };

  const revoke = async (id: string) => {
    setBusy(id);
    try {
      notifySourceRevoke(await adminApi.revoke(id));
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    try {
      notifySourceRevoke(await adminApi.deleteUser(id));
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  return (
    <section className="space-y-3">
      {users.length === 0 ? (
        <p className="text-sm text-muted">{t("admin_no_users")}</p>
      ) : (
        <ul className="space-y-1.5">
          {users.map((u) => {
            const name = [u.firstname, u.lastname].filter(Boolean).join(" ") || u.id.slice(0, 8);
            const blocked = u.access_status === "blocked";
            // "connected" = el usuario tiene alguna fuente de datos vinculada (no identidad).
            const dataSources = ["strava", "wahoo", "garmin", "oura", "fitbit"];
            const userProviders = (u.providers ?? "").split(",").map((p) => p.trim());
            const connected = dataSources.some((p) => userProviders.includes(p));
            const isBusy = busy === u.id;
            return (
              <li
                key={u.id}
                className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-panel px-3 py-2"
              >
                <span className="text-sm font-semibold text-fg">{name}</span>
                <TypeBadge type={u.account_type} />
                {(u.codes ?? []).map((c) => (
                  <span
                    key={c.code}
                    className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-semibold ${
                      c.kind === "beta"
                        ? "bg-accent/15 text-accent"
                        : "bg-indigo-500/15 text-indigo-500"
                    }`}
                  >
                    {c.code}
                  </span>
                ))}
                <span
                  className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                    connected ? "bg-emerald-500/15 text-emerald-500" : "bg-line/60 text-muted"
                  }`}
                  title={connected ? t("admin_connected_hint") : t("admin_disconnected_hint")}
                >
                  {connected ? t("admin_connected") : t("admin_disconnected")}
                </span>
                {blocked && (
                  <span className="rounded-md bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-500">
                    {t("admin_blocked")}
                  </span>
                )}
                <span className="text-[11px] text-muted">
                  {u.activity_count} {t("admin_activities")}
                  {" · "}
                  {u.last_sync_at ? u.last_sync_at.slice(0, 10) : t("admin_never")}
                </span>

                <div className="ml-auto flex items-center gap-1.5">
                  <label className="flex items-center gap-1 text-[11px] text-muted" title={lang === "es" ? "Admin" : "Admin"}>
                    <input
                      type="checkbox"
                      checked={!!u.is_admin}
                      disabled={isBusy}
                      onChange={(e) => run(u.id, () => adminApi.setAdmin(u.id, e.target.checked))}
                      className="accent-violet-500"
                    />
                    Admin
                  </label>
                  <select
                    value={u.account_type === "coach" ? "coach" : "athlete"}
                    disabled={isBusy}
                    onChange={(e) =>
                      run(u.id, () => adminApi.setAccountType(u.id, e.target.value as "athlete" | "coach"))
                    }
                    className="rounded-md border border-line bg-ink/40 px-1.5 py-0.5 text-[11px] text-fg"
                  >
                    <option value="athlete">{t("admin_type_athlete")}</option>
                    <option value="coach">{t("admin_type_coach")}</option>
                  </select>
                  <button
                    onClick={() => run(u.id, () => adminApi.setAccess(u.id, blocked ? "active" : "blocked"))}
                    disabled={isBusy}
                    className="rounded-md border border-line px-2 py-0.5 text-[11px] font-medium text-muted transition hover:text-fg disabled:opacity-50"
                  >
                    {blocked ? t("admin_unblock") : t("admin_block")}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(t("admin_revoke_confirm"))) void revoke(u.id);
                    }}
                    disabled={isBusy}
                    className="rounded-md border border-red-500/40 px-2 py-0.5 text-[11px] font-medium text-red-500 transition hover:bg-red-500/10 disabled:opacity-50"
                  >
                    {t("admin_revoke")}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(t("admin_delete_confirm"))) void remove(u.id);
                    }}
                    disabled={isBusy}
                    title={t("admin_delete")}
                    aria-label={t("admin_delete")}
                    className="rounded-md border border-red-500/40 px-2 py-0.5 text-[11px] font-medium text-red-500 transition hover:bg-red-500/20 disabled:opacity-50"
                  >
                    🗑
                  </button>
                  <button
                    onClick={async () => {
                      const toId = prompt(lang === "es"
                        ? `Fusionar "${name}" EN otro usuario. Introduce el ID del usuario destino (se conserva):`
                        : `Merge "${name}" INTO another user. Enter the destination user ID (will be kept):`);
                      if (!toId?.trim()) return;
                      if (!confirm(lang === "es"
                        ? `¿Fusionar ${name} (FROM) en ${toId.trim()} (TO)? Esta acción no se puede deshacer.`
                        : `Merge ${name} (FROM) into ${toId.trim()} (TO)? This cannot be undone.`)) return;
                      setBusy(u.id);
                      try {
                        const res = await adminApi.mergeUsers(u.id, toId.trim());
                        if (!res.ok) alert(`Error: ${res.error ?? "unknown"}`);
                        onChanged();
                      } finally { setBusy(null); }
                    }}
                    disabled={isBusy}
                    title={lang === "es" ? "Fusionar en…" : "Merge into…"}
                    className="rounded-md border border-violet-500/40 px-2 py-0.5 text-[11px] font-medium text-violet-500 transition hover:bg-violet-500/10 disabled:opacity-50"
                  >
                    ⇢
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
